/**
 * Core API client with automatic authentication handling
 * This client automatically adds authentication headers to all requests
 * and handles token refresh when needed
 */

import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { getAccessToken, refreshSession } from '@/utils/auth';

/**
 * Enhanced API client that automatically handles authentication
 */
class AuthenticatedApiClient {
  /**
   * Helper method to get authentication headers
   */
  private async getAuthHeaders(): Promise<Record<string, string>> {
    try {
      const session = await refreshSession();
      const token = getAccessToken(session);
      return token ? { Authorization: `Bearer ${token}` } : {};
    } catch (error) {
      console.error('Error getting auth headers:', error);
      // Try to get from localStorage as fallback
      try {
        const sessionData = localStorage.getItem('session');
        if (sessionData) {
          const session = JSON.parse(sessionData);
          const token = getAccessToken(session);
          return token ? { Authorization: `Bearer ${token}` } : {};
        }
      } catch (fallbackError) {
        console.error('Fallback auth failed:', fallbackError);
      }
      return {};
    }
  }

  /**
   * Merge auth headers with any provided headers
   */
  private async prepareConfig(config?: AxiosRequestConfig): Promise<AxiosRequestConfig> {
    const authHeaders = await this.getAuthHeaders();
    return {
      ...config,
      headers: {
        ...authHeaders,
        ...config?.headers,
      },
    };
  }

  /**
   * GET request with automatic auth
   */
  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    const preparedConfig = await this.prepareConfig(config);
    return axios.get<T>(url, preparedConfig);
  }

  /**
   * POST request with automatic auth
   */
  async post<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    const preparedConfig = await this.prepareConfig(config);
    return axios.post<T>(url, data, preparedConfig);
  }

  /**
   * PUT request with automatic auth
   */
  async put<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    const preparedConfig = await this.prepareConfig(config);
    return axios.put<T>(url, data, preparedConfig);
  }

  /**
   * PATCH request with automatic auth
   */
  async patch<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    const preparedConfig = await this.prepareConfig(config);
    return axios.patch<T>(url, data, preparedConfig);
  }

  /**
   * DELETE request with automatic auth
   */
  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    const preparedConfig = await this.prepareConfig(config);
    return axios.delete<T>(url, preparedConfig);
  }

  /**
   * HEAD request with automatic auth
   */
  async head<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    const preparedConfig = await this.prepareConfig(config);
    return axios.head<T>(url, preparedConfig);
  }

  /**
   * OPTIONS request with automatic auth
   */
  async options<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    const preparedConfig = await this.prepareConfig(config);
    return axios.options<T>(url, preparedConfig);
  }
}

// Export singleton instance
export const authenticatedApi = new AuthenticatedApiClient();

// Also export the class for cases where multiple instances might be needed
export { AuthenticatedApiClient };

// Export a default instance as well for convenience
export default authenticatedApi;